import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  CreativeReportAd,
  CreativeReportColumn,
  CreativeReportMediaItem,
  CreativeReportRow,
  ServerPaginationState,
  ViewMode,
} from "../types";
import AdsCreativeTable from "./table/AdsCreativeTable";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
  memo,
  useCallback,
} from "react";
import MediaPreview from "./preview/MediaPreview";
import CreativeCardGrid from "./card/CreativeCardGrid";
import { LuX } from "react-icons/lu";
import { ScrollableFadeContainer } from "./table/CreativeTable";
import ViewToggle from "./configs/ViewToggle";
import { CreativeReportMetadata, CreativeSectionConfiguration } from "@/types";
import { SqButton } from "@/components/squircle-ui/button";
import { formatDateRange } from "@/utils/dateUtils";
import { useGlobalStore } from "@/GlobalStore";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { RiMegaphoneLine } from "@remixicon/react";
import { fetchAdDetailData } from "../apiService";
import { LuLoaderCircle } from "react-icons/lu";

type Props = {
  open: boolean;
  setOpen: (open: boolean) => void;
  columns: CreativeReportColumn[] | undefined;
  ad_columns: CreativeReportColumn[] | undefined;
  row: CreativeReportRow | null;
  sectionName: string;
  initialViewMode?: ViewMode;
  status: string;
  reportMetadata?: CreativeReportMetadata | null;
  uniqueLabels?: string[];
  isV2?: boolean;
  accountId?: string;
  sectionId?: string;
  sectionConfiguration?: CreativeSectionConfiguration;
  groupByKey?: string;
};

export const GroupSheet = memo(function GroupSheet(props: Props) {
  const {
    open,
    setOpen,
    columns,
    ad_columns,
    row,
    sectionName,
    initialViewMode,
    status = "success",
    reportMetadata,
    uniqueLabels,
    isV2 = false,
    accountId,
    sectionId,
    sectionConfiguration,
    groupByKey,
  } = props;
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);
  const [previewMediaItem, setPreviewMediaItem] = useState<CreativeReportMediaItem>();
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode ?? "table_view");

  const [v2Ads, setV2Ads] = useState<CreativeReportAd[]>([]);
  const [v2AdColumns, setV2AdColumns] = useState<CreativeReportColumn[]>([]);
  const [v2ServerPagination, setV2ServerPagination] =
    useState<ServerPaginationState | null>(null);
  const [v2TotalRow, setV2TotalRow] = useState<
    Omit<CreativeReportRow, "ads"> | undefined
  >(undefined);
  const [v2Loading, setV2Loading] = useState(false);
  const [v2PageLoading, setV2PageLoading] = useState(false);
  const prevGroupValueRef = useRef<string | null>(null);

  const resetV2State = useCallback(() => {
    setV2Ads([]);
    setV2AdColumns([]);
    setV2ServerPagination(null);
    setV2TotalRow(undefined);
  }, []);

  const currentGroupValue = useMemo(() => {
    if (!isV2 || !row || !groupByKey) return null;
    const value = (row as Record<string, unknown>)[groupByKey];
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    // For objects or arrays, return JSON stringified version, else empty string.
    if (value !== undefined && value !== null) {
      try {
        return JSON.stringify(value);
      } catch {
        return "";
      }
    }
    return "";
  }, [isV2, row, groupByKey]);

  const fetchAdsForGroup = useCallback(
    async (page: number = 1, pageSize: number = 25, showFullLoader?: boolean) => {
      if (
        !isV2 ||
        !accountId ||
        !sectionId ||
        !sectionConfiguration ||
        !currentGroupValue
      )
        return;
      if (showFullLoader) {
        setV2Loading(true);
      } else {
        setV2PageLoading(true);
      }

      try {
        const response = await fetchAdDetailData({
          accountId,
          sectionId,
          sectionData: sectionConfiguration,
          group_value: currentGroupValue,
          ad_page: page,
          ad_page_size: pageSize,
        });

        if (response.status === "success") {
          setV2Ads(response.ads);
          if (response.ad_columns?.length) {
            setV2AdColumns(response.ad_columns);
          }
          setV2ServerPagination({
            page: response.pagination.page,
            pageSize: response.pagination.page_size,
            totalCount: response.pagination.total_ads,
            totalPages: response.pagination.total_pages,
          });
          if (response.total) {
            setV2TotalRow(response.total);
          }
        }
      } catch (error) {
        console.error("Error fetching ad details:", error);
      } finally {
        setV2Loading(false);
        setV2PageLoading(false);
      }
    },
    [isV2, accountId, sectionId, sectionConfiguration, currentGroupValue],
  );

  const handleV2AdPageChange = useCallback(
    (page: number) => {
      void fetchAdsForGroup(page, v2ServerPagination?.pageSize ?? 25);
    },
    [fetchAdsForGroup, v2ServerPagination?.pageSize],
  );

  const handleV2AdPageSizeChange = useCallback(
    (pageSize: number) => {
      void fetchAdsForGroup(1, pageSize, false);
    },
    [fetchAdsForGroup],
  );

  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      setViewMode(mode);
      if (mode === "table_view") {
        handleV2AdPageSizeChange(25);
      }
    },
    [handleV2AdPageSizeChange],
  );

  const primaryColumns = columns?.filter((column) => column.is_primary);
  const sequenceData = useMemo(() => {
    if (!row) return undefined;
    const key = Object.keys(row as Record<string, unknown>).find((k) =>
      k.endsWith("_sequences"),
    );
    const value = key ? (row as Record<string, unknown>)[key] : undefined;
    return Array.isArray(value)
      ? (value as Array<{ label: string; order: number }>)
      : undefined;
  }, [row]);
  const hasSequence = !!(sequenceData && sequenceData.length > 0);
  const [selectedDateView, setSelectedDateView] = useState<string>("compare");
  const prevOpenRef = useRef(open);

  // Clean up on close
  useEffect(() => {
    if (!open && prevOpenRef.current) {
      setPreviewMediaItem(undefined);
      setSelectedDateView("compare");
      if (isV2) {
        resetV2State();
        prevGroupValueRef.current = null;
      }
    }
    prevOpenRef.current = open;
  }, [open, isV2, resetV2State]);

  // Sync viewMode before paint to avoid flash of wrong view
  useLayoutEffect(() => {
    if (open && initialViewMode) {
      setViewMode(initialViewMode);
    }
  }, [open, initialViewMode, row]);

  useEffect(() => {
    if (!open || !isV2 || !currentGroupValue) return;

    if (currentGroupValue !== prevGroupValueRef.current) {
      prevGroupValueRef.current = currentGroupValue;
      resetV2State();
      void fetchAdsForGroup(1, 25, true);
    }
  }, [open, isV2, currentGroupValue, resetV2State, fetchAdsForGroup]);

  const resolvedAds = useMemo(() => {
    if (!isV2) {
      return (selectedDateView === "previous" ? row?.ads_previous : row?.ads) ?? [];
    }
    if (selectedDateView !== "previous") return v2Ads;
    return v2Ads.map((ad) => {
      const swapped: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(ad)) {
        if (k.endsWith("_previous") || k.endsWith("_change") || k.endsWith("_percent_change")) continue;
        const prevVal = (ad as Record<string, unknown>)[`${k}_previous`];
        swapped[k] = prevVal !== undefined ? prevVal : v;
      }
      return swapped as CreativeReportAd;
    });
  }, [isV2, selectedDateView, v2Ads, row]);

  const resolvedTotalRow = useMemo(() => {
    if (!isV2 || selectedDateView !== "previous" || !v2TotalRow) return v2TotalRow;
    const swapped: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(v2TotalRow)) {
      if (k.endsWith("_previous") || k.endsWith("_change") || k.endsWith("_percent_change")) continue;
      const prevVal = (v2TotalRow as Record<string, unknown>)[`${k}_previous`];
      swapped[k] = prevVal !== undefined ? prevVal : v;
    }
    return swapped as Omit<CreativeReportRow, "ads">;
  }, [isV2, selectedDateView, v2TotalRow]);

  const resolvedAdColumns = isV2 && v2AdColumns.length > 0 ? v2AdColumns : ad_columns;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        showCloseButton={false}
        className="min-w-[78vw] h-full p-0 focus-visible:outline-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="h-full overflow-auto relative flex flex-col px-[24px] pt-[35px] pb-4">
          <div className="absolute top-[10px] right-[19px] flex items-center gap-[14px]">
            {reportMetadata?.compare_date && (
              <Select value={selectedDateView} onValueChange={setSelectedDateView}>
                <SelectTrigger className="flex items-center min-w-[280px] h-8 squircle squircle-radius-[8px] squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-border-gray border-0 bg-transparent shadow-none drop-shadow-sm">
                  <div className="flex items-center">
                    <RiMegaphoneLine
                      size={14}
                      color="#9aa3bd"
                      className="shrink-0 mr-2"
                    />
                    <div className="translate-y-[0.5px]">
                      {selectedDateView === "compare" ? (
                        <>
                          <span className="text-[#021859CC] font-medium text-[11px] leading-[19.16px] tracking-[0.02em] mr-1">
                            {formatDateRange({
                              ...reportMetadata?.date,
                              timezone: selectedAccount?.time_zone,
                            })}
                          </span>
                          <span className="font-normal not-italic text-[11px] leading-[19.16px] tracking-[0.02em] text-[#02185966] mr-1">
                            vs
                          </span>
                          <span className="font-normal not-italic text-[11px] leading-[19.16px] tracking-[0.02em] text-[#021859CC] mr-2">
                            {formatDateRange({
                              ...reportMetadata?.compare_date,
                              timezone: selectedAccount?.time_zone,
                            })}
                          </span>
                        </>
                      ) : selectedDateView === "current" ? (
                        <>
                          <span className="text-[#021859CC] font-medium text-[11px] leading-[19.16px] tracking-[0.02em] mr-1">
                            Current Period
                          </span>
                          <span className="font-normal not-italic text-[11px] leading-[19.16px] tracking-[0.02em] text-[#021859CC] mr-2">
                            {formatDateRange({
                              ...reportMetadata?.date,
                              timezone: selectedAccount?.time_zone,
                            })}
                          </span>
                        </>
                      ) : (
                        selectedDateView === "previous" && (
                          <>
                            <span className="text-[#021859CC] font-medium text-[11px] leading-[19.16px] tracking-[0.02em] mr-1">
                              Previous Period
                            </span>
                            <span className="font-normal not-italic text-[11px] leading-[19.16px] tracking-[0.02em] text-[#021859CC] mr-2">
                              {formatDateRange({
                                ...reportMetadata?.compare_date,
                                timezone: selectedAccount?.time_zone,
                              })}
                            </span>
                          </>
                        )
                      )}
                    </div>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="compare" hideItemIndicator>
                    <span className="font-medium text-[12px] leading-[20px] tracking-[0] text-[#021859CC] mr-2">
                      {formatDateRange({
                        ...reportMetadata?.date,
                        timezone: selectedAccount?.time_zone,
                      })}
                    </span>
                    <span className="font-normal text-[12px] leading-[20px] tracking-[0] text-[#021859CC] mr-2">
                      vs
                    </span>
                    <span className="font-normal text-[12px] leading-[20px] tracking-[0] text-[#021859CC]">
                      {formatDateRange({
                        ...reportMetadata?.compare_date,
                        timezone: selectedAccount?.time_zone,
                      })}
                    </span>
                  </SelectItem>
                  <SelectItem value="current" hideItemIndicator>
                    <span className="font-medium text-[12px] leading-[20px] tracking-[0] text-[#021859CC] mr-2">
                      Current Period
                    </span>
                    <span className="font-normal text-[12px] leading-[20px] tracking-[0] text-[#021859CC]">
                      {formatDateRange({
                        ...reportMetadata?.date,
                        timezone: selectedAccount?.time_zone,
                      })}
                    </span>
                  </SelectItem>
                  <SelectItem value="previous" hideItemIndicator>
                    <span className="font-medium text-[12px] leading-[20px] tracking-[0] text-[#021859CC] mr-2">
                      Previous Period
                    </span>
                    <span className="font-normal text-[12px] leading-[20px] tracking-[0] text-[#021859CC]">
                      {formatDateRange({
                        ...reportMetadata?.compare_date,
                        timezone: selectedAccount?.time_zone,
                      })}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
            <ViewToggle
              view={viewMode}
              onViewChange={handleViewModeChange}
              metadata={{ line_chart_view: { hide: true } }}
            />
            <SqButton
              variant="outline"
              onClick={() => setOpen(false)}
              size={"md"}
              className="inline-flex  items-center gap-[6px] bg-white"
            >
              <LuX className="w-[14px] h-[14px] flex-shrink-0 text-[#D92D20]" />
              <span className="overflow-hidden text-brand-primary-new text-ellipsis h-[16px] text-[12px] font-normal leading-0 tracking-[0.24px]">
                Close
              </span>
            </SqButton>
          </div>
          {/* Title Section */}
          <div className="flex flex-col gap-[12px] mt-4">
            <div>
              {sectionName && (
                <span className="overflow-hidden text-brand-primary-800 text-ellipsis text-[20px] font-normal leading-[30.651px] tracking-[0.4px]">
                  {sectionName}
                  {!hasSequence && primaryColumns && primaryColumns.length > 0
                    ? " / "
                    : null}
                </span>
              )}
              {!hasSequence && (
                <span
                  className={`overflow-hidden text-brand-primary-new text-ellipsis text-[20px] font-normal tracking-[0.4px]`}
                >
                  {row?.[primaryColumns?.[0]?.key as keyof CreativeReportRow] as string}
                </span>
              )}
            </div>
            {!hasSequence &&
              primaryColumns?.slice(1).map((column) => (
                <div key={column.key}>
                  <span className="overflow-hidden text-[rgba(2,24,89,0.60)] text-ellipsis text-base font-normal leading-6 tracking-[0.32px]">
                    {row?.[column.key as keyof CreativeReportRow] as string}
                  </span>
                </div>
              ))}
          </div>

          {/*Data Section */}
          <div className="flex-1 mt-2">
            {hasSequence && (
              <div className="mb-2">
                <ScrollableFadeContainer
                  sequenceData={sequenceData}
                  isScrollable={false}
                  uniqueLabels={uniqueLabels}
                />
              </div>
            )}
            {isV2 && v2Loading ? (
              <div className="flex items-center justify-center py-16">
                <LuLoaderCircle className="w-6 h-6 animate-spin text-brand-primary-new/60" />
              </div>
            ) : (
              resolvedAds &&
              resolvedAdColumns &&
              (viewMode === "table_view" ? (
                <AdsCreativeTable
                  data={resolvedAds}
                  columns={resolvedAdColumns.filter((c) => c.hidden_from_table !== true)}
                  setPreviewMediaItem={setPreviewMediaItem}
                  status={status}
                  reportMetadata={
                    selectedDateView === "compare"
                      ? reportMetadata
                      : selectedDateView === "previous"
                        ? { date: reportMetadata?.compare_date ?? undefined }
                        : { date: reportMetadata?.date }
                  }
                  totalRow={isV2 ? resolvedTotalRow : (row ?? undefined)}
                  serverPagination={isV2 ? (v2ServerPagination ?? undefined) : undefined}
                  onServerPageChange={isV2 ? handleV2AdPageChange : undefined}
                  onServerPageSizeChange={isV2 ? handleV2AdPageSizeChange : undefined}
                  isPaginationLoading={v2PageLoading}
                />
              ) : (
                <CreativeCardGrid
                  data={resolvedAds}
                  columns={resolvedAdColumns}
                  ad_columns={resolvedAdColumns}
                  setGroupSheetOpen={() => { }}
                  setSelectedAdGroup={() => { }}
                  isAdView={true}
                  allowPagination={true}
                  showBlankCard={false}
                  selectedDateView={selectedDateView}
                  forcedAspectRatio="4:5"
                  serverPagination={isV2 ? (v2ServerPagination ?? undefined) : undefined}
                  onServerPageChange={isV2 ? handleV2AdPageChange : undefined}
                  onServerPageSizeChange={isV2 ? handleV2AdPageSizeChange : undefined}
                  isPaginationLoading={v2PageLoading}
                />
              ))
            )}
          </div>
          {previewMediaItem && (
            <MediaPreview
              mediaItem={previewMediaItem}
              setMediaItem={setPreviewMediaItem}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
});
